/**
 * Boot sequence:
 *   1. Seed the API base + SSO redirect window globals (build-time fallbacks).
 *   2. Fetch API-served runtime config (`/ui-config`) and populate
 *      `window.__SPS_*` globals.
 *   3. Dynamically import the app tree (`./app/mount.js`) so module-level
 *      config reads happen AFTER the runtime config is resolved.
 */

import { initBrowserTelemetry } from "@repo/observability-browser";
import { reloadOnStaleChunk } from "@repo/ui/event-listeners";

import { resolveRuntimeConfig } from "./lib/runtime-config-loader.js";

declare global {
  interface Window {
    __SPS_API_BASE?: string;
    __SPS_SSO_WEB_BASE_URL?: string;
    __SPS_AUTH_REDIRECT_URI?: string;
    __SPS_SENTRY_DSN?: string;
    __SPS_SENTRY_ENVIRONMENT?: string;
  }
}

reloadOnStaleChunk();

window.__SPS_API_BASE =
  window.__SPS_API_BASE ?? (import.meta.env["VITE_SPS_API_BASE"] as string | undefined) ?? "/api";
window.__SPS_SSO_WEB_BASE_URL =
  window.__SPS_SSO_WEB_BASE_URL ??
  (import.meta.env["VITE_SSO_WEB_BASE_URL"] as string | undefined) ??
  undefined;
window.__SPS_AUTH_REDIRECT_URI =
  window.__SPS_AUTH_REDIRECT_URI ??
  (import.meta.env["VITE_AUTH_REDIRECT_URI"] as string | undefined) ??
  undefined;

void resolveRuntimeConfig().finally(() => {
  // After the runtime config, before the app tree: this image is promoted across
  // environments, so a baked-in DSN would report every environment as one.
  // `resolveRuntimeConfig` never throws, so nothing is lost by waiting for it.
  initBrowserTelemetry({
    dsn: window.__SPS_SENTRY_DSN,
    environment: window.__SPS_SENTRY_ENVIRONMENT,
    instrumentRouter: false,
  });

  void import("./app/mount.js").then(({ mountApp }) => {
    mountApp();
  });
});
